import { useEffect, useState } from 'react';

import {
  Button,
  Icon,
  Modal,
  PageHeader,
  Restricted,
  Switch,
  Table,
  TableColumn,
} from '@components';
import { RepositoryParams } from '@services/api';
import { AccessGroup } from 'interfaces/AccessGroup';
import AccessGroupAPICaller, {
  AccessGroupFilters,
} from 'services/api/accessGroups';

import { useForm } from 'react-hook-form';
import { AccessForm } from './AccessForm';
import { AccessUsersTable } from './AccessUsersTable';

export default function AccessPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<{ total: number; result: AccessGroup[] }>({
    total: 0,
    result: [],
  });
  const [filters, setFilters] = useState<AccessGroupFilters>({ order: 'name' });
  const [isOpenModalAccess, setIsOpenModalAccess] = useState(false);
  const [editId, setEditId] = useState('');
  const [expandedId, setExpandedId] = useState('');

  const { setError } = useForm();

  const fetch = (filters: RepositoryParams) => {
    setIsLoading(true);
    AccessGroupAPICaller.fetchGroups(filters)
      .then((values) => {
        setData(values);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    fetch(filters);
  }, [filters]);

  const activeOrInactiveUser = async (data: any, active: boolean) => {
    AccessGroupAPICaller.createOrUpdate(
      {
        ...data,
        active,
      },
      setError
    ).then(() => fetch(filters));
  };

  return (
    <>
      <div className="p-s-300">
        <PageHeader title="Níveis de Acesso" />

        <Table
          showColumnSelector={false}
          isLoading={isLoading}
          hoverable={false}
          expandedRow={expandedId}
          totalRecords={data && data.total ? data.total : 0}
          data={data && data.result ? data.result : []}
          withSearchInput
          withPagination
          filters={filters}
          setFilters={setFilters}
          additionalFilters={
            <Restricted context="groups" resource="create">
              <div className="d-flex justify-end">
                <Button
                  className="ml-s-100"
                  suffixes={<Icon>add</Icon>}
                  design="filled"
                  onClick={() => {
                    setEditId('');
                    setIsOpenModalAccess(true);
                  }}
                >
                  Novo nível de acesso
                </Button>
              </div>
            </Restricted>
          }
          renderExpandedRow={(group) => (
            <AccessUsersTable
              key={group.id}
              groupName={group.name}
              groupId={group.id}
              onUpdate={() => fetch(filters)}
            />
          )}
        >
          <TableColumn fromKey="name" header="Nome" />
          <TableColumn
            fromKey="active"
            header="Status"
            render={(_, row) => {
              return (
                <Switch
                  label=""
                  caption={row.active ? 'Ativo' : 'Inativo'}
                  defaultChecked={row.active}
                  onChange={() => activeOrInactiveUser(row, !row.active)}
                />
              );
            }}
          />
          <TableColumn
            width="6%"
            fromKey="id"
            header="Ações"
            disableSorting
            render={(id) => {
              return (
                <div className="d-flex">
                  <Restricted context="groups" resource="update">
                    <Button
                      weight="bold"
                      design="transparent"
                      prefixes={<Icon>edit</Icon>}
                      onClick={() => {
                        setEditId(id);
                        setIsOpenModalAccess(true);
                      }}
                    />
                  </Restricted>
                  <Button
                    weight="bold"
                    design="transparent"
                    prefixes={
                      <Icon>
                        {expandedId === id ? 'expand_less' : 'expand_more'}
                      </Icon>
                    }
                    onClick={() => {
                      if (expandedId === id) return setExpandedId('');
                      else setExpandedId(id);
                    }}
                  />
                </div>
              );
            }}
          />
        </Table>
      </div>

      <Modal
        size="large"
        isOpen={isOpenModalAccess}
        onClickAway={() => {
          setEditId('');
          setIsOpenModalAccess(false);
        }}
        title={
          editId === ''
            ? 'Cadastrar níveis de acesso'
            : 'Editar níveis de acesso'
        }
      >
        <AccessForm
          editId={editId}
          onSave={() => fetch(filters)}
          closeModal={() => {
            setEditId('');
            setIsOpenModalAccess(false);
          }}
        />
      </Modal>
    </>
  );
}
